import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  useTheme,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle,
  Language as LanguageIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store';
import { toggleSidebar, addNotification } from '@/store/slices/uiSlice';
import { logout } from '@/store/slices/authSlice';
import { authService } from '@/services/authService';

const AppHeader: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation('navigation');
  const { pageTitle } = useAppSelector((state) => state.ui);
  const { user } = useAppSelector((state) => state.auth);
  
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);

  // User profile data comes from FusionAuth login response
  // No need to fetch separately - login response includes: id, email, firstName, lastName, roles
  // The getUserProfile() call is skipped to avoid unnecessary 401 errors

  const handleMenuClick = () => {
    dispatch(toggleSidebar());
  };

  const handleUserMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfileClick = () => {
    navigate('/profile');
    handleUserMenuClose();
  };

  const handleLogout = async () => {
    // Close menu immediately to prevent UI issues during logout
    handleUserMenuClose();
    
    try {
      await authService.logout();
      dispatch(logout());
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      
      // Notify user about the logout error
      dispatch(addNotification({
        message: 'There was an issue logging out from the server, but you have been logged out locally.',
        severity: 'warning',
        autoHideDuration: 6000
      }));
      
      // Force logout even if service call fails
      dispatch(logout());
      navigate('/login');
    }
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: theme.zIndex.drawer + 1,
        backgroundColor: theme.palette.primary.main,
      }}
    >
      <Toolbar>
        <IconButton
          color="inherit"
          aria-label={t('menu')}
          edge="start"
          onClick={handleMenuClick}
          sx={{
            marginRight: 2,
            minWidth: 44,
            minHeight: 44,
          }}
        >
          <MenuIcon />
        </IconButton>

        <Typography
          variant="h6"
          noWrap
          component="h1"
          sx={{
            flexGrow: 1,
            fontWeight: 500,
          }}
        >
          {pageTitle || 'Compliance Portal'}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {user && (
            <Typography
              variant="body2"
              sx={{
                marginRight: 2,
                color: 'rgba(255, 255, 255, 0.8)',
                display: { xs: 'none', sm: 'block' },
              }}
            >
              {(user.firstName || user.lastName) 
                ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() 
                : 'User'
              }
            </Typography>
          )}
          
          <IconButton
            color="inherit"
            aria-label="Change language"
            sx={{
              marginRight: 1,
              minWidth: 44,
              minHeight: 44,
            }}
          >
            <LanguageIcon />
          </IconButton>

          <IconButton
            color="inherit"
            aria-label="User account menu"
            onClick={handleUserMenuClick}
            sx={{
              minWidth: 44,
              minHeight: 44,
            }}
          >
            <AccountCircle />
          </IconButton>
          
          <Menu
            anchorEl={anchorEl}
            open={isMenuOpen}
            onClose={handleUserMenuClose}
            onClick={handleUserMenuClose}
            PaperProps={{
              elevation: 0,
              sx: {
                overflow: 'visible',
                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                mt: 1.5,
                '& .MuiAvatar-root': {
                  width: 32,
                  height: 32,
                  ml: -0.5,
                  mr: 1,
                },
                '&:before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  right: 14,
                  width: 10,
                  height: 10,
                  bgcolor: 'background.paper',
                  transform: 'translateY(-50%) rotate(45deg)',
                  zIndex: 0,
                },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            {user && (
              <Box sx={{ px: 2, py: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Signed in as
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {user.email?.trim() || 'Email not available'}
                </Typography>
              </Box>
            )}
            <Divider />
            <MenuItem onClick={handleProfileClick}>
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Profile</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Logout</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default AppHeader;